import React, { useState, useEffect } from "react";
import Layout from "../../components/Layout/Layout";
import { useAuthState } from 'react-firebase-hooks/auth';
import { Table, Input, Button, Tag, Tooltip } from 'antd';
import { auth } from '../../firebase/firebase-config';
import BeatLoader from "react-spinners/BeatLoader";
import { ConfirmModal } from "../../components";
import { 
  FileAddOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  ImportOutlined, 
  LinkOutlined 
} from '@ant-design/icons';
import { toast } from "react-toastify";
import { 
  getAllLabTests, 
  deleteLabTest 
} from "../../services/labtests";
import AddLabTestModal from "./AddLabTestModal/AddLabTestModal";
import ImportLabTestsModal from "./ImportLabTestsModal/ImportLabTestsModal";
import AssignLabTestModal from "./AssignLabTestModal/AssignLabTestModal";
import "./LabTestsPage.css";

const { Search } = Input;

const override = {
  marginTop: "20px",
};

function LabTestsPage() {
  const [allLabTests, setAllLabTests] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [filteredData, setFilteredData] = useState([]);
  const [editData, setEditData] = useState(null);
  const [selectedLabTestId, setSelectedLabTestId] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [currentActiveId, setCurrentActiveId] = useState();
  const [user] = useAuthState(auth);
  const [loading, setLoading] = useState(false);

  const handleDelete = (id) => {
    setCurrentActiveId(id);
    setShowConfirmModal(true);
  };

  const confirmDelete = async () => {
    try {
      await deleteLabTest(currentActiveId);
      toast.success('Lab test deleted successfully');
      fetchData();
      setShowConfirmModal(false);
    } catch (error) {
      toast.error('Error deleting lab test');
    }
  };

  const handleEdit = (labTestData) => {
    setEditData(labTestData);
    setShowCreateModal(true);
  };

  const handleAssign = (labTestId) => {
    setSelectedLabTestId(labTestId);
    setShowAssignModal(true);
  };

  const toggleAddModal = () => {
    setEditData(null);
    setShowCreateModal(!showCreateModal);
  };

  const toggleImportModal = () => {
    setShowImportModal(!showImportModal);
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await getAllLabTests();
      console.log('API response:', response);
      
      // Handle different response structures - ensure we always have an array
      let labTests = [];
      if (response && response.data) {
        // Check if data is directly in response.data or in response.data.data
        if (Array.isArray(response.data)) {
          labTests = response.data;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          labTests = response.data.data;
        }
      }
      
      setAllLabTests(labTests);
    } catch (error) {
      console.error('Error fetching lab tests:', error);
      toast.error('Error fetching lab tests');
      // Initialize with empty array on error
      setAllLabTests([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    // Make sure we're filtering an array
    const dataToFilter = Array.isArray(allLabTests) ? allLabTests : [];
    const filtered = filterData(dataToFilter, searchText);
    setFilteredData(filtered);
  }, [allLabTests, searchText]);

  const filterData = (data, query) => {
    if (!Array.isArray(data)) return [];
    if (!query) return data;

    return data.filter((item) =>
      Object.values(item || {}).some((value) =>
        value?.toString().toLowerCase().includes(query.toLowerCase())
      )
    );
  };

  const handleSearch = (value) => {
    setSearchText(value);
  };

  const formatPrice = (min, max) => {
    if (min === max || !max) {
      return `${min?.toLocaleString() || 0} FCFA`;
    }
    return `${min?.toLocaleString() || 0} - ${max?.toLocaleString() || 0} FCFA`;
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => {
        if (!a.name || !b.name) return 0;
        return a.name.localeCompare(b.name);
      },
      render: (text, record) => (
        <div className="labtest-name-cell">
          <span className="labtest-name">{text || 'No name'}</span>
          {record.nameFr && (
            <span className="labtest-name-fr">{record.nameFr}</span>
          )}
        </div>
      ),
    },
    {
      title: 'Price Range',
      key: 'priceRange',
      render: (_, record) => (
        <Tag color="green">
          {formatPrice(record.minPrice, record.maxPrice)}
        </Tag>
      ),
      sorter: (a, b) => {
        const minA = a.minPrice || 0;
        const minB = b.minPrice || 0;
        return minA - minB;
      },
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (text) => text || 'No description',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <div className="action-buttons">
          <Tooltip title="Edit Lab Test">
            <Button 
              type="text" 
              icon={<EditOutlined />} 
              onClick={() => handleEdit(record)} 
              className="action-button" 
            />
          </Tooltip>
          <Tooltip title="Assign to Facility">
            <Button 
              type="text" 
              icon={<LinkOutlined />} 
              onClick={() => handleAssign(record.id)} 
              className="action-button" 
            />
          </Tooltip>
          <Tooltip title="Delete Lab Test">
            <Button 
              type="text" 
              danger 
              icon={<DeleteOutlined />} 
              onClick={() => handleDelete(record.id)} 
              className="action-button" 
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  // Expanded row render function
  const expandedRowRender = (record) => {
    return (
      <div className="expanded-row">
        {record.description && (
          <div className="expanded-section">
            <h4>Description</h4>
            <p>{record.description}</p>
            {record.descriptionFr && (
              <p className="secondary-text">{record.descriptionFr}</p>
            )}
          </div>
        )}

        <div className="expanded-section">
          <h4>Price Information</h4>
          <p>Minimum Price: <strong>{record.minPrice?.toLocaleString() || 0} FCFA</strong></p>
          <p>Maximum Price: <strong>{record.maxPrice?.toLocaleString() || 0} FCFA</strong></p>
        </div>

        {record.createdAt && (
          <div className="expanded-section">
            <h4>Created</h4>
            <p>{new Date(record.createdAt).toLocaleString()}</p>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <Layout title="Lab Tests" pageTitle="All Lab Tests">
        <div className="section">
          <div className="parent-con">
            <div className="data-table">
              <div className="top">
                <div className="span">
                  <h3>All Lab Tests</h3>
                </div>
                <div className="action-group">
                  <Button
                    type="primary"
                    icon={<ImportOutlined />}
                    onClick={toggleImportModal}
                    className="btn-import"
                  >
                    Import
                  </Button>
                  <Button
                    type="primary"
                    icon={<FileAddOutlined />}
                    onClick={toggleAddModal}
                    className="btn-add"
                  >
                    Add Lab Test
                  </Button>
                </div>
              </div>
              <div className="table-con">
                <div style={{ textAlign: "center" }}>
                  <BeatLoader
                    color="#3080ED"
                    loading={loading}
                    cssOverride={override}
                  />
                </div>
                <Search
                  placeholder="Search lab tests..."
                  onSearch={handleSearch}
                  onChange={(e) => handleSearch(e.target.value)}
                  value={searchText}
                  style={{ width: 300, marginBottom: 16 }}
                />

                <Table
                  columns={columns}
                  dataSource={Array.isArray(filteredData) ? filteredData : []}
                  rowKey={(record) => record.id || Math.random().toString()}
                  loading={loading}
                  expandable={{
                    expandedRowRender,
                    expandRowByClick: true,
                  }}
                  pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} lab tests`
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </Layout>

      {showCreateModal && (
        <AddLabTestModal
          editData={editData}
          onClose={() => {
            setShowCreateModal(false);
            setEditData(null);
          }}
          onContentAdded={() => {
            setShowCreateModal(false);
            fetchData();
          }}
        />
      )}

      {showImportModal && (
        <ImportLabTestsModal
          onClose={() => setShowImportModal(false)}
          onImportComplete={() => {
            setShowImportModal(false);
            fetchData();
          }}
        />
      )}

      {showAssignModal && (
        <AssignLabTestModal
          labTestId={selectedLabTestId}
          onClose={() => {
            setShowAssignModal(false);
            setSelectedLabTestId(null);
          }}
          onAssignComplete={() => {
            setShowAssignModal(false);
            toast.success('Lab test assigned successfully');
          }}
        />
      )}
      
      {showConfirmModal && (
        <ConfirmModal
          title="Are you sure you want to delete this lab test?"
          onAccept={() => confirmDelete()}
          onCancel={() => setShowConfirmModal(false)}
        />
      )}
    </>
  );
}

export default LabTestsPage;